import { FormControlLabel, Grid, Radio, RadioGroup } from "@mui/material";
import dayjs from "dayjs";
import {
  FormikHandlers,
  FormikHelpers,
  FormikState,
  FormikValues,
} from "formik";
import { AsyncSelectValue } from "../../../AsyncSelect";
import CustomDatePicker from "../../../CustomDatePicker";
import CustomSelect from "../../../CustomSelect";
import ErrorHelper from "../../../ErrorWrapper";
import Flex from "../../../Flex";
import NumberInput from "../../../NumberInput";
import Text from "../../../Text";
import WeekdayChooser from "./WeekChooser";

export const RecurringConfig: React.FC<{
  formik: Formik<FormValues>;
  required?: boolean;
}> = ({ formik, required = false }) => {
  const { setFieldValue } = formik;
  return (
    <Grid item container xs={12} spacing={2} rowSpacing={1}>
      <Grid item xs={12}>
        <CustomSelect
          required={required}
          value={formik.values.recurringFrequency}
          label="Frequency"
          name="recurringFrequency"
          placeholder="Frequency of recurring"
          options={recurringFrequencySelectItems}
          onChange={(value) => setFieldValue("recurringFrequency", value)}
          error={
            formik.touched.recurringFrequency &&
            (formik.errors.recurringFrequency as any)
          }
        />
      </Grid>

      <RecurringEventEndConfig formik={formik} />

      {formik.values.recurringFrequency === "custom" && (
        <CustomFrequencyConfig formik={formik} required={required} />
      )}
    </Grid>
  );
};

const RecurringEventEndConfig: React.FC<{
  formik: Formik<FormValues>;
}> = ({ formik }) => {
  return (
    <Grid item container xs={12}>
      <Grid item sm={7}>
        <Flex gap={1} alignItems={"center"} ml={0.5}>
          <Text>
            Ends<sup style={{ color: "red" }}>*</sup>:
          </Text>

          <ErrorHelper
            error={formik.touched.isRecurring && formik.errors.endType}
          >
            <RadioGroup
              row
              name="endType"
              onBlur={formik.handleBlur}
              defaultValue={formik.values.endType}
              onChange={(e) => {
                formik.setFieldValue("endType", e.currentTarget.value);
                if (e.currentTarget.value === "after_n_events") {
                  formik.setFieldValue("endDate", null);
                }
              }}
            >
              {recurringEventEndTypeRadioItems.map((props) => (
                <FormControlLabel
                  defaultChecked={props.value === formik.values.endType}
                  value={props.value}
                  control={<Radio />}
                  label={props.label}
                />
              ))}
            </RadioGroup>
          </ErrorHelper>
        </Flex>
      </Grid>
      <Grid item sm={5} mt={1}>
        {formik.values.endType === "on_date" && (
          // <DateInput
          //   label="End on"
          //   error={formik.errors.endDate && `${formik.errors.endDate}`}
          //   name="endDate"
          //   onChange={handleChange("endDate")}
          //   value={formik.values.endDate}
          // />
          <CustomDatePicker
            label="End On"
            // format="YYYY-MM-DD"
            required
            name="endDate"
            handleBlur={formik.handleBlur}
            minDate={dayjs(formik.values?.startTime)}
            value={
              formik.values.endDate ? dayjs(formik.values.endDate) : undefined
            }
            onChange={(val) => {
              formik.setFieldValue("endDate", val);
            }}
            error={formik.touched.endDate && (formik.errors.endDate as any)}
          />
        )}

        {formik.values.endType === "after_n_events" && (
          <NumberInput
            required
            min={0}
            textFieldProps={{
              name: "endAfter",
              onBlur: formik.handleBlur,
              value: formik.values.endAfter,
              onChange: formik.handleChange,
            }}
            label="End after these number"
            error={formik.touched.endAfter && (formik.errors.endAfter as any)}
          />
        )}
      </Grid>
    </Grid>
  );
};

type Formik<Value> = FormikState<Value> & FormikHelpers<Value> & FormikHandlers;

const CustomFrequencyConfig: React.FC<{
  formik: Formik<FormValues>;
  required?: boolean;
}> = ({ formik, required = false }) => {
  return (
    <>
      <Grid mt="lg" container spacing={1} marginTop={"5px"} pl={1}>
        <Grid item sm={6} justifyContent={"center"}>
          <NumberInput
            required={required}
            textFieldProps={{
              name: "customFrequency",
              onBlur: formik.handleBlur,
              value: formik.values.customFrequency,
              onChange: formik.handleChange,
            }}
            label="Repeat every"
            error={
              formik.touched.customFrequency &&
              (formik.errors.customFrequency as any)
            }
            // min={1}
            // precision={0}
            // step={1}
          />
        </Grid>
        <Grid item sm={6}>
          <CustomSelect
            required={required}
            value={formik.values.customFrequencyUnit}
            error={
              formik.touched.customFrequencyUnit &&
              (formik.errors.customFrequencyUnit as any)
            }
            label=""
            name="customFrequencyUnit"
            handleBlur={formik.handleBlur}
            options={customFrequencyTypeSelectItems}
            onChange={(value) =>
              formik.setFieldValue("customFrequencyUnit", value)
            }
          />
        </Grid>
      </Grid>

      {formik.values.customFrequencyUnit === "weeks" && (
        <Grid item xs={12}>
          <WeekdayChooser
            label="Select the days to recur on"
            selectedDays={formik.values.selectedWeekdays}
            onChange={(value) =>
              formik.setFieldValue("selectedWeekdays", value)
            }
            error={formik.errors.selectedWeekdays}
          />
        </Grid>
      )}
    </>
  );
};

type SelectItem<T> = {
  value: T;
  label: string;
};

export type RecurringFrequency = "daily" | "weekly" | "monthly" | "custom";
export type CustomFrequencyUnit = "days" | "weeks" | "months";
export type EndType = "never" | "on_date" | "after_n_events";

const customFrequencyTypeSelectItems: SelectItem<CustomFrequencyUnit>[] = [
  {
    value: "days",
    label: "Days",
  },
  {
    value: "weeks",
    label: "Weeks",
  },
  {
    value: "months",
    label: "Months",
  },
];

const recurringFrequencySelectItems: SelectItem<RecurringFrequency>[] = [
  {
    value: "daily",
    label: "Daily",
  },
  {
    value: "weekly",
    label: "Weekly",
  },
  {
    value: "monthly",
    label: "Monthly",
  },
  {
    value: "custom",
    label: "Custom",
  },
];
interface FormValues extends FormikValues {
  patientId: { value: string; label: string; birthDate: string } | undefined;
  visit_type: string;
  type: string;
  paymentType: "CASH" | "INSURANCE";
  locationId?: { value: string; label: string };
  providerSpecialities?: any;
  providerId: AsyncSelectValue | undefined;
  startTime: string;
  endTime: Date;
  insurance_type: string;
  note: string;
  primary_insurance?: { uuid: string };
  secondary_insurance?: { uuid: string };
  authorization: string;
  forms: string[];
  sendForms: boolean;

  isRecurring: boolean;
  recurringFrequency: RecurringFrequency;
  reminder_set: boolean;

  endType: EndType;
  endDate: Date;
  endAfter: number;

  customFrequency: number;
  customFrequencyUnit: CustomFrequencyUnit;
  selectedWeekdays: number[];
  reminders?: {
    reminderBeforeNumber: number;
    reminderBeforeUnit: string;
    reminderMedium: string[];
  };
}

const recurringEventEndTypeRadioItems: SelectItem<EndType>[] = [
  {
    value: "on_date",
    label: "On",
  },
  {
    value: "after_n_events",
    label: "After",
  },
];
